import {
  UserMenu as UserProfile,
  Text,
  Separator,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "snap-design-system";
import { formatIsoDate, getInitials } from "~/helpers";
import { USER_CONSTANTS } from "~/helpers/constants";
import { useUserData, useUserCredits } from "~/store/userStore";
import { useDialogActions } from "~/store/dialogStore";
import { Info } from "lucide-react";
import { ConsultasInfoDialog } from "./ConsultasInfoDialog";

interface UserMenuProps {
  userMenuContent: React.ReactNode;
}

export default function UserMenu({ userMenuContent }: UserMenuProps) {
  const { openDialog } = useDialogActions();
  const userData = useUserData();
  const userImage = userData?.[USER_CONSTANTS.user_data.image as keyof typeof userData] as string;
  const userName = userData?.[USER_CONSTANTS.user_data.name as keyof typeof userData] as string;
  const userCredits = useUserCredits();
  const initials = getInitials(userName);
  const userCreditsResetDate = userData?.[USER_CONSTANTS.user_data.next_reset_credits as keyof typeof userData] as string;
  const userProfile = userData?.[USER_CONSTANTS.user_data.role as keyof typeof userData] as string;
  const isUserStandalone = userProfile === USER_CONSTANTS.profile_types.standalone;

  const getValidImageUrl = (image: string | null | undefined) => {
    return image || undefined;
  };

  const handleOpenConsultasInfo = () => {
    openDialog({
      title: "INFORMAÇÕES SOBRE CONSULTAS",
      content: <ConsultasInfoDialog.Content />,
      footer: <ConsultasInfoDialog.Footer />,
      className: "max-w-xl",
    });
  };

  const renderSubtitle = () => {
    return (
      <div className="inline-flex items-center gap-1">
        <Text variant={"body-sm"} color="secondary" as="span">{userCredits} CONSULTAS</Text>
        <Tooltip delayDuration={0}>
          <TooltipTrigger className="cursor-help" asChild>
            <Info size={14} className="text-accent-500" />
          </TooltipTrigger>
          <TooltipContent side="bottom" variant="chamfered" chamfer="bottomRight">
            <div className="px-4 py-2">
              <Text variant={"body-sm"} as="div">
                Você possui <span className="font-bold"><span className="text-accent-500">{!isUserStandalone ? "*" : ""}</span>{` ${userCredits} consultas `}</span>
              </Text>
              {userCreditsResetDate ? (
                <Text variant={"body-sm"} as="div">
                  válidas até a data <span className="font-semibold">{formatIsoDate(userCreditsResetDate, true)}</span>.
                </Text>
              ) : null}
              {!isUserStandalone && (
                <div>
                  <Separator className="my-2 border-border" />
                  <Text variant={"body-sm"} as="div">
                    <span className="font-bold text-accent-500">*</span> Saiba mais sobre as consultas disponíveis{" "}
                    <span
                      className="text-accent-500 underline cursor-pointer"
                      onClick={handleOpenConsultasInfo}
                      title="Abrir mais informações de consultas"
                    >
                      clicando aqui.
                    </span>
                  </Text>
                </div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  };

  return (
    <UserProfile
      userName={userName || "Sem nome"}
      userSubtitle={renderSubtitle()}
      avatarSrc={getValidImageUrl(userImage)}
      avatarFallback={initials || "NA"}
      avatarSize="small"
    >
      {userMenuContent}
    </UserProfile>
  );
}
